import React, { useState } from 'react';
import { TrendingUp, Users, DollarSign, BarChart3, Target, Globe, Zap, Award, Building, Rocket, Star, Activity } from 'lucide-react';

const KeyMetrics = ({ formData, onInputChange }) => {
  const [selectedStage, setSelectedStage] = useState(formData.metrics?.stage || 'idea');

  const fundingStages = [
    {
      id: 'idea',
      name: 'Idea Stage',
      description: 'Concept validation and market research',
      icon: Lightbulb,
      metrics: [
        {
          id: 'marketSize',
          label: 'Total Addressable Market (TAM)',
          icon: Globe,
          placeholder: 'e.g., $50B',
          description: 'Total market size for your solution'
        },
        {
          id: 'problemValidation',
          label: 'Problem Validation',
          icon: Target,
          placeholder: 'e.g., 500+ interviews conducted',
          description: 'Evidence that the problem exists'
        },
        {
          id: 'solutionFit',
          label: 'Solution-Market Fit',
          icon: Zap,
          placeholder: 'e.g., 80% of users would pay',
          description: 'Validation that your solution solves the problem'
        }
      ]
    },
    {
      id: 'pre-seed',
      name: 'Pre-Seed',
      description: 'Building MVP and initial traction',
      icon: Rocket,
      metrics: [
        {
          id: 'mvpUsers',
          label: 'MVP Users',
          icon: Users,
          placeholder: 'e.g., 1,000+',
          description: 'Number of users testing your MVP'
        },
        {
          id: 'userRetention',
          label: 'User Retention',
          icon: TrendingUp,
          placeholder: 'e.g., 60% D1, 30% D7',
          description: 'User retention rates'
        },
        {
          id: 'feedback',
          label: 'User Feedback Score',
          icon: Star,
          placeholder: 'e.g., 4.5/5',
          description: 'Average user satisfaction score'
        },
        {
          id: 'partnerships',
          label: 'Strategic Partnerships',
          icon: Building,
          placeholder: 'e.g., 3 partnerships',
          description: 'Number of key partnerships'
        }
      ]
    },
    {
      id: 'seed',
      name: 'Seed',
      description: 'Product-market fit and early revenue',
      icon: Seed,
      metrics: [
        {
          id: 'monthlyUsers',
          label: 'Monthly Active Users',
          icon: Users,
          placeholder: 'e.g., 10K+',
          description: 'Monthly active users'
        },
        {
          id: 'monthlyRevenue',
          label: 'Monthly Recurring Revenue',
          icon: DollarSign,
          placeholder: 'e.g., $50K',
          description: 'Monthly recurring revenue'
        },
        {
          id: 'growthRate',
          label: 'Growth Rate',
          icon: TrendingUp,
          placeholder: 'e.g., 150% YoY',
          description: 'Year-over-year growth rate'
        },
        {
          id: 'customerAcquisition',
          label: 'Customer Acquisition Cost',
          icon: Target,
          placeholder: 'e.g., $25',
          description: 'Cost to acquire a new customer'
        }
      ]
    },
    {
      id: 'series-a',
      name: 'Series A',
      description: 'Scaling and market expansion',
      icon: SeriesA,
      metrics: [
        {
          id: 'annualRevenue',
          label: 'Annual Recurring Revenue',
          icon: DollarSign,
          placeholder: 'e.g., $2M',
          description: 'Annual recurring revenue (ARR)'
        },
        {
          id: 'customerCount',
          label: 'Customer Count',
          icon: Users,
          placeholder: 'e.g., 500+',
          description: 'Total number of paying customers'
        },
        {
          id: 'netRetention',
          label: 'Net Revenue Retention',
          icon: TrendingUp,
          placeholder: 'e.g., 120%',
          description: 'Net revenue retention rate'
        },
        {
          id: 'burnRate',
          label: 'Burn Rate',
          icon: Activity,
          placeholder: 'e.g., $100K/month',
          description: 'Monthly burn rate'
        },
        {
          id: 'runway',
          label: 'Cash Runway',
          icon: Target,
          placeholder: 'e.g., 18 months',
          description: 'How long current cash will last'
        }
      ]
    },
    {
      id: 'series-b',
      name: 'Series B',
      description: 'Market leadership and international expansion',
      icon: SeriesB,
      metrics: [
        {
          id: 'arr',
          label: 'Annual Recurring Revenue',
          icon: DollarSign,
          placeholder: 'e.g., $10M+',
          description: 'Annual recurring revenue (ARR)'
        },
        {
          id: 'enterpriseCustomers',
          label: 'Enterprise Customers',
          icon: Building,
          placeholder: 'e.g., 50+',
          description: 'Number of enterprise customers'
        },
        {
          id: 'international',
          label: 'International Markets',
          icon: Globe,
          placeholder: 'e.g., 15 countries',
          description: 'Number of international markets'
        },
        {
          id: 'teamSize',
          label: 'Team Size',
          icon: Users,
          placeholder: 'e.g., 100+ employees',
          description: 'Total team size'
        },
        {
          id: 'marketShare',
          label: 'Market Share',
          icon: Award,
          placeholder: 'e.g., 15%',
          description: 'Market share in target segment'
        }
      ]
    },
    {
      id: 'custom',
      name: 'Custom Metrics',
      description: 'Define your own key metrics',
      icon: BarChart3,
      metrics: [
        {
          id: 'custom1',
          label: 'Custom Metric 1',
          icon: Target,
          placeholder: 'Enter your metric',
          description: 'Define your own key metric'
        },
        {
          id: 'custom2',
          label: 'Custom Metric 2',
          icon: TrendingUp,
          placeholder: 'Enter your metric',
          description: 'Define your own key metric'
        },
        {
          id: 'custom3',
          label: 'Custom Metric 3',
          icon: DollarSign,
          placeholder: 'Enter your metric',
          description: 'Define your own key metric'
        },
        {
          id: 'custom4',
          label: 'Custom Metric 4',
          icon: Users,
          placeholder: 'Enter your metric',
          description: 'Define your own key metric'
        }
      ]
    }
  ];

  const currentStage = fundingStages.find(stage => stage.id === selectedStage);

  const handleStageChange = (stageId) => {
    setSelectedStage(stageId);
    onInputChange('metrics', 'stage', stageId);
  };

  const handleMetricChange = (metricId, value) => {
    onInputChange('metrics', metricId, value);
  };

  return (
    <div className="space-y-6">
      {/* Stage Selection */}
      <div className="bg-gradient-to-br from-blue-50 to-purple-50 rounded-2xl p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Funding Stage</h3>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
          {fundingStages.map((stage) => {
            const Icon = stage.icon;
            return (
              <button
                key={stage.id}
                onClick={() => handleStageChange(stage.id)}
                className={`p-3 rounded-xl text-center transition-all ${
                  selectedStage === stage.id
                    ? 'bg-gradient-to-r from-blue-600 to-purple-600 text-white shadow-lg'
                    : 'bg-white text-gray-700 hover:bg-gray-50 border border-gray-200'
                }`}
              >
                <Icon className="w-6 h-6 mx-auto mb-2" />
                <div className="text-xs font-medium">{stage.name}</div>
              </button>
            );
          })}
        </div>
      </div>

      {/* Stage Description */}
      {currentStage && (
        <div className="bg-blue-50 rounded-xl p-4">
          <div className="flex items-center gap-3 mb-2">
            <currentStage.icon className="w-5 h-5 text-blue-600" />
            <h4 className="font-semibold text-blue-900">{currentStage.name}</h4>
          </div>
          <p className="text-sm text-blue-700">{currentStage.description}</p>
        </div>
      )}

      {/* Metrics Grid */}
      {currentStage && (
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
          {currentStage.metrics.map((metric) => {
            const Icon = metric.icon;
            const isCustom = metric.id.startsWith('custom');
            const labelKey = `${metric.id}_label`;
            const customLabel = formData.metrics?.[labelKey] || metric.label;
            return (
              <div key={metric.id} className="bg-gradient-to-br from-blue-50 to-purple-50 rounded-2xl p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="p-2 bg-white rounded-xl shadow-sm">
                    <Icon className="h-5 w-5 text-blue-600" />
                  </div>
                  <div>
                    {isCustom ? (
                      <>
                        <input
                          type="text"
                          value={formData.metrics?.[labelKey] || ''}
                          onChange={(e) => handleMetricChange(labelKey, e.target.value)}
                          className="block w-full rounded font-medium text-gray-700 border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 bg-white/50 mb-1"
                          placeholder={metric.label}
                        />
                        <p className="text-xs text-gray-500">{metric.description}</p>
                      </>
                    ) : (
                      <>
                        <label className="block text-sm font-medium text-gray-700">
                          {metric.label}
                        </label>
                        <p className="text-xs text-gray-500">{metric.description}</p>
                      </>
                    )}
                  </div>
                </div>
                <input
                  type="text"
                  value={formData.metrics?.[metric.id] || ''}
                  onChange={(e) => handleMetricChange(metric.id, e.target.value)}
                  className="block w-full rounded-xl border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 bg-white/50"
                  placeholder={metric.placeholder}
                />
              </div>
            );
          })}
        </div>
      )}

      {/* Tips Section */}
      <div className="mt-8 p-4 bg-blue-50 rounded-xl">
        <h3 className="text-sm font-medium text-blue-800 mb-2">Tips for Key Metrics</h3>
        <ul className="text-sm text-blue-700 space-y-2">
          <li>• Choose metrics that align with your current funding stage</li>
          <li>• Be specific and accurate with your numbers</li>
          <li>• Use consistent formatting (e.g., $ for currency, % for percentages)</li>
          <li>• Include time periods where relevant (e.g., "150% YoY growth")</li>
          <li>• Focus on metrics that demonstrate traction and growth potential</li>
          <li>• Update metrics regularly as your business evolves</li>
        </ul>
      </div>
    </div>
  );
};

// Custom icons for funding stages
const Lightbulb = (props) => (
  <svg {...props} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
  </svg>
);

const Seed = (props) => (
  <svg {...props} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
  </svg>
);

const SeriesA = (props) => (
  <svg {...props} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
  </svg>
);

const SeriesB = (props) => (
  <svg {...props} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
  </svg>
);

export default KeyMetrics; 